import { PayloadAction } from '@reduxjs/toolkit';
import {
  activeFilesCollectionCleared,
  activeFilesCollectionUpdated,
  activeWhiteboardsCollectionCleared,
  activeWhiteboardsCollectionUpdated,
} from 'features/content-library/contentLibrarySlice';
import {
  gridPanelClosed,
  lockedContentChanged,
} from 'features/layout/features/content/contentSlice';
import { GridPanelName } from 'features/layout/features/content/types';
import { screenshareCollectionAdded } from 'features/layout/features/order/orderSlice';
import { RoomJoinedPayload } from 'features/room/types';
import { call, put } from 'redux-saga/effects';
import { RTCClient } from 'utils/webrtc';
import {
  ProcessedScreenshares,
  processScreensharesForRoom,
} from 'features/layout/utils/processScreenshares';
import {
  ProcessedFiles,
  processOpenedFilesForRoom,
} from 'features/layout/utils/processOpenedFiles';
import {
  ProcessedWhiteboards,
  processOpenedWhiteboardsForRoom,
} from 'features/layout/utils/processOpenedWhiteboards';
import {
  ActiveContent,
  determineActiveContent,
} from 'features/layout/utils/determineActiveContent';
import { initializeGridPanelSaga } from 'features/layout/sagas/initializeGridPanelSaga';
import { remoteZoomSynced } from 'features/content-zoom/contentZoomSlice';
import { buildZoomId, fromServerScale } from 'features/content-zoom/utils/formatUtils';

export function* prepareExposedContentSaga(action: PayloadAction<RoomJoinedPayload>) {
  const {
    users,
    openedWhiteboards,
    openedFiles,
    lockedContent,
    user: { id: localUserId },
  } = action.payload;

  const availableTabs: Record<GridPanelName, boolean> = {
    contentLibrary: false,
    screenshare: false,
    whiteboard: false,
    external: false,
  };

  const screensharesResult: ProcessedScreenshares = yield call(
    processScreensharesForRoom,
    users,
    lockedContent,
    localUserId
  );

  yield call(
    RTCClient.receivingFeed.updateAvailableScreenshares,
    screensharesResult.availableScreenshares
  );

  if (screensharesResult.collection.length) {
    yield put(
      screenshareCollectionAdded(
        screensharesResult.collection.sort((a, b) => b.timestamp - a.timestamp)
      )
    );
    availableTabs.screenshare = true;
  } else {
    yield put(screenshareCollectionAdded([]));
  }

  const filesResult: ProcessedFiles = yield call(
    processOpenedFilesForRoom,
    openedFiles,
    lockedContent
  );

  if (openedFiles) {
    yield put(activeFilesCollectionUpdated(filesResult.collection));
    availableTabs.contentLibrary = true;
  } else {
    yield put(activeFilesCollectionCleared());
  }

  const whiteboardsResult: ProcessedWhiteboards = yield call(
    processOpenedWhiteboardsForRoom,
    openedWhiteboards,
    lockedContent
  );

  if (openedWhiteboards) {
    yield put(activeWhiteboardsCollectionUpdated(whiteboardsResult.collection));
    availableTabs.whiteboard = true;
  } else {
    yield put(activeWhiteboardsCollectionCleared());
  }

  const activeContent: ActiveContent = yield call(
    determineActiveContent,
    screensharesResult,
    filesResult,
    whiteboardsResult,
    lockedContent
  );

  if (lockedContent && activeContent.shouldLockContent) {
    yield put(
      lockedContentChanged({
        ...lockedContent,
        page: activeContent.openedFile?.page,
      })
    );
  }

  if (activeContent.activeTab) {
    yield call(
      initializeGridPanelSaga,
      activeContent.activeTab,
      activeContent.openedFile,
      activeContent.openedWhiteboard,
      availableTabs
    );
  } else {
    yield put(gridPanelClosed());
  }

  if (lockedContent && activeContent.shouldLockContent) {
    const { scale, offsetX, offsetY } = lockedContent;

    yield put(
      remoteZoomSynced({
        zoomId: buildZoomId(lockedContent.id, activeContent.openedFile?.page),
        scale: fromServerScale(scale),
        offsetX: offsetX ?? 0,
        offsetY: offsetY ?? 0,
      })
    );
  }
}
